
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { ShoppingBag } from 'lucide-react';
import MobileHeader from '../components/mobile/MobileHeader';
import { useAuth } from '../contexts/AuthContext';
import CartCheckoutModal from '../components/cart/CartCheckoutModal';
import CartLayoutWeb from '../components/cart/CartLayoutWeb';
import CartLayoutMobile from '../components/cart/CartLayoutMobile';
import { notify } from '../lib/toast';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';




// Helper to group items by seller
const groupItemsBySeller = (items: any[]) => {
    const groups: Record<string, any[]> = {};
    items.forEach(item => {
        if (!groups[item.sellerId]) {
            groups[item.sellerId] = [];
        }
        groups[item.sellerId].push(item);
    });
    return groups;
};

const CartPage: React.FC = () => {
    const { t } = useTranslation('marketplace');
    const navigate = useNavigate();
    const { items, removeFromCart, totalItems, clearCart } = useCart();
    const { currentUser } = useAuth();

    // Checkout State
    const [checkoutSellerId, setCheckoutSellerId] = useState<string | null>(null);
    const [checkoutSellerName, setCheckoutSellerName] = useState<string>('');
    const [checkoutAmount, setCheckoutAmount] = useState<number>(0);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

    // Responsive State
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate totals
    const cartArray = Object.values(items);
    const totalPrice = cartArray.reduce((sum, item) => sum + (item.priceAtAdd || 0), 0);
    const sellers = groupItemsBySeller(cartArray);

    const handleCheckout = (sellerId: string | null, sellerName: string, amount: number) => {
        if (!sellerId && Object.keys(sellers).length > 1) {
            notify.error(t('cart.error.multiple_sellers'));
            return;
        }

        // If sellerId null but single seller, use that one
        let targetSellerId = sellerId;
        if (!targetSellerId && Object.keys(sellers).length === 1) {
            targetSellerId = Object.keys(sellers)[0];
        }

        if (targetSellerId) {
            setCheckoutSellerId(targetSellerId);
            setCheckoutSellerName(sellerName);
            setCheckoutAmount(amount);
            setIsCheckoutOpen(true);
        }
    };

    if (totalItems === 0) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-32">
                {isMobile ? <MobileHeader title={t('cart.title')} showBack /> : null}

                <div className="flex flex-col items-center justify-center min-h-[70vh] px-8 text-center space-y-8">
                    <div className="relative group">
                        <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all scale-150"></div>
                        <motion.div
                            className="relative w-48 h-48 bg-white dark:bg-gray-900 rounded-[3rem] shadow-xl border border-gray-100 dark:border-gray-800 flex items-center justify-center overflow-hidden"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6, ease: "backOut" }}
                        >
                            <motion.div
                                animate={{
                                    y: [0, -10, 0],
                                    rotate: [0, 5, -5, 0]
                                }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <ShoppingBag size={80} className="text-gray-200 dark:text-gray-800" strokeWidth={1} />
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                    animate={{
                                        scale: [0.8, 1.1, 0.8],
                                        opacity: [0.3, 0.6, 0.3]
                                    }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <div className="w-12 h-12 bg-blue-500/10 rounded-full blur-xl" />
                                </motion.div>
                            </motion.div>
                            {/* Floating coins or elements */}
                            {[...Array(3)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
                                    animate={{
                                        y: [-20, -100],
                                        x: [0, (i - 1) * 30],
                                        opacity: [0, 1, 0],
                                        scale: [0, 1, 0]
                                    }}
                                    transition={{
                                        duration: 2,
                                        delay: i * 0.5,
                                        repeat: Infinity,
                                        ease: "circOut"
                                    }}
                                    style={{ bottom: '20%' }}
                                />
                            ))}
                        </motion.div>
                    </div>
                    <div>
                        <h2 className="text-3xl font-black text-gray-950 dark:text-white uppercase tracking-tighter italic">{t('cart.empty.title')}</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-3 font-medium text-lg max-w-sm mx-auto">{t('cart.empty.subtitle')}</p>
                    </div>
                    <Link to="/" className="w-full max-w-xs py-5 bg-gray-950 dark:bg-white text-white dark:text-gray-950 rounded-2xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-105 active:scale-95 transition-all text-sm">
                        {t('cart.empty.action')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            {isMobile ? (
                <CartLayoutMobile
                    sellers={sellers}
                    totalPrice={totalPrice}
                    totalItems={totalItems}
                    onRemove={removeFromCart}
                    onCheckout={handleCheckout}
                    onClearCart={clearCart}
                />
            ) : (
                <CartLayoutWeb
                    sellers={sellers}
                    totalPrice={totalPrice}
                    totalItems={totalItems}
                    onRemove={removeFromCart}
                    onCheckout={handleCheckout}
                    onClearCart={clearCart}
                />
            )}

            {/* Checkout Modal (Global) */}
            <CartCheckoutModal
                isOpen={isCheckoutOpen}
                onClose={() => setIsCheckoutOpen(false)}
                items={checkoutSellerId && sellers[checkoutSellerId] ? sellers[checkoutSellerId] : []}
                sellerId={checkoutSellerId || ''}
                sellerName={checkoutSellerName}
                totalAmount={checkoutAmount}
                currentUser={currentUser}
            />
        </React.Fragment>
    );
};

export default CartPage;
